import { Legend } from "./Legend";

export interface Segment {
	key: string;
	value: number;
	/** CSS rang ifodasi — `holatVar()` yoki `var(--chart-*)`. */
	color: string;
	label: string;
}

export interface SegmentBarProps {
	segments: Segment[];
	height?: number;
	/** Standart `true` — legend qiymati bilan (relief qoidasi). */
	showLegend?: boolean;
	/** `true` — legendda sanoq o'rniga foiz (yig'indisi aynan 100). */
	percent?: boolean;
}

/**
 * Butun foizlar — eng katta qoldiq usuli, yig'indisi AYNAN 100.
 * Oddiy `Math.round` uchta teng bo'lakda 33+33+33 = 99 berardi.
 */
export function butunFoizlar(qiymatlar: number[]): number[] {
	const jami = qiymatlar.reduce((a, b) => a + b, 0);

	if (!(jami > 0)) {
		return qiymatlar.map(() => 0);
	}

	const xom = qiymatlar.map((qiymat) => (qiymat / jami) * 100);
	const natija = xom.map(Math.floor);
	let qolgan = 100 - natija.reduce((a, b) => a + b, 0);
	const tartib = xom
		.map((qiymat, indeks) => ({ indeks, qoldiq: qiymat - Math.floor(qiymat) }))
		.sort((a, b) => b.qoldiq - a.qoldiq || a.indeks - b.indeks);

	for (const { indeks } of tartib) {
		if (qolgan <= 0) {
			break;
		}

		natija[indeks] = (natija[indeks] ?? 0) + 1;
		qolgan -= 1;
	}

	return natija;
}

/**
 * 100% ga to'ldirilgan bo'lakli chiziq (sof CSS).
 *
 * ⚠️ **Jami nol — bo'lak CHIZILMAYDI.** `0/0 = NaN` va `width: NaN%`
 * bo'laklarni jimgina yo'qotardi (dashboard `SplitBar` izohi).
 *
 * ⚠️ **Kengliklar yig'indisi AYNAN 100** — oxirgi chizilgan bo'lak
 * qoldiqni oladi, suzuvchi nuqta xatosi o'ngda bo'shliq qoldirmaydi.
 *
 * ⚠️ Nol qiymatli bo'lak chiziqda yo'q, lekin legendada QOLADI —
 * «Kelmagan 0» ham ma'lumot.
 */
export function SegmentBar({
	segments,
	height = 10,
	showLegend = true,
	percent = false,
}: SegmentBarProps) {
	const jami = segments.reduce((a, s) => a + Math.max(0, s.value), 0);
	const foizlar = butunFoizlar(segments.map((s) => Math.max(0, s.value)));
	const chiziladigan = jami > 0 ? segments.filter((s) => s.value > 0) : [];
	let yigindi = 0;
	const kengliklar = chiziladigan.map((s, indeks) => {
		if (indeks === chiziladigan.length - 1) {
			return Math.round((100 - yigindi) * 100) / 100;
		}

		const kenglik = Math.round((s.value / jami) * 100 * 100) / 100;
		yigindi += kenglik;
		return kenglik;
	});
	const tavsif = segments.map((s) => `${s.label}: ${s.value}`).join(", ");

	return (
		<div>
			<div
				role="img"
				aria-label={tavsif}
				className="flex overflow-hidden rounded-full"
				style={{ height: `${height}px`, backgroundColor: "var(--chart-grid)" }}
			>
				{chiziladigan.map((s, indeks) => (
					<i
						key={s.key}
						data-segment
						title={`${s.label}: ${s.value} (${foizlar[segments.indexOf(s)] ?? 0}%)`}
						className="block h-full"
						style={{ width: `${kengliklar[indeks] ?? 0}%`, backgroundColor: s.color }}
					/>
				))}
			</div>

			{showLegend ? (
				<div className="mt-2">
					<Legend
						items={segments.map((s, indeks) => ({
							label: s.label,
							color: s.color,
							value: percent ? `${foizlar[indeks] ?? 0}%` : String(s.value),
						}))}
					/>
				</div>
			) : null}
		</div>
	);
}
